import { FC, useMemo, useState } from "react";
import { Box, Button, Chip, FormControl, List, ListItemText, SxProps, TextField, Tooltip, Theme } from "@mui/material";
import { NavLink } from "react-router-dom";
import theme, { HoverLinkSX } from "theme";
import { P } from "theme/typography";
import { useScriptsLoader } from "loaders/scripts.loader";
import { Navigate, Outlet } from "react-router";
import { ScriptSecretContext } from "routes/lens-editor/scripts/ScriptSecretContext";
import { Warning } from "@mui/icons-material";
import { NewScriptDialog } from "./scripts/NewScriptDialog";
import { useParamNumbers } from "helpers/hooks/useParamNumbers";
import { NewGreys } from "theme/palette";
import { scriptHasFlag as scriptHasFlag } from "./scripts/LensScriptFlag";
import { LensScriptFlag } from "generated/graphql";

const NavSX: SxProps<Theme> = {
  ...HoverLinkSX,
  color: "grey.900",
};

const Scripts: FC = () => {
  const { lensId } = useParamNumbers(["lensId"]);

  if (lensId === undefined) {
    return <Navigate to=".." />;
  }

  const scripts = useScriptsLoader();
  const scriptsSecretHandle = useState("");
  const [search, setSearch] = useState("");
  const filteredScripts = useMemo(
    () => scripts.filter((s) => s.name.toLowerCase().indexOf(search.toLowerCase()) !== -1),
    [search, scripts]
  );
  const [showNewScript, setShowNewScript] = useState(false);

  return (
    <ScriptSecretContext.Provider value={scriptsSecretHandle}>
      {showNewScript && <NewScriptDialog lensId={lensId} close={() => setShowNewScript(false)} />}
      <Box sx={{ display: "grid", gridTemplateColumns: "250px 1fr", gap: 1, height: "100%", pt: 2 }}>
        <Box
          sx={{
            px: 2,
            display: "flex",
            flexDirection: "column",
            overflow: "auto",
            height: "100%",
            backgroundColor: NewGreys.background1,
            pt: 1,
          }}>
          <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
            <P variant="lgBold" sx={{ mr: 2 }}>
              Scripts
            </P>
            <Button size="small" color="primary" variant="contained" onClick={() => setShowNewScript(true)}>
              New
            </Button>
          </Box>
          <FormControl fullWidth>
            <TextField label="Search Scripts" value={search} onChange={(evt) => setSearch(evt.target.value)} />
          </FormControl>
          <List
            dense
            sx={{
              overflowY: "auto",
              "a.active span, a.active svg": {
                color: theme.palette.primary.main,
              },
            }}>
            {filteredScripts.map((script) => (
              <NavLink key={script.name} to={`${script.name}`}>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    ":hover": {
                      backgroundColor: theme.palette.grey["100"],
                    },
                    px: 1,
                  }}>
                  <P sx={NavSX}>
                    <ListItemText primary={script.name} />
                  </P>
                  {scriptHasFlag(script, LensScriptFlag.Auto) && script.isApproved && (
                    <Chip
                      label="Auto"
                      color="success"
                      sx={{
                        ".MuiChip-label": {
                          color: "white!important",
                        },
                      }}
                      size="small"
                    />
                  )}
                  {!script.isApproved && (
                    <Tooltip title="Approval Required">
                      <Warning sx={{ color: theme.palette.grey["400"] }} />
                    </Tooltip>
                  )}
                </Box>
              </NavLink>
            ))}
          </List>
        </Box>
        <Box sx={{ height: "100%", minHeight: 0, minWidth: 0 }}>
          <Outlet />
        </Box>
      </Box>
    </ScriptSecretContext.Provider>
  );
};

export default Scripts;
